import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Router } from '@angular/router'
import {
  DataContextBuilder,
  ElderToastService,
  IDataContext,
  Sort,
  SortUtil,
  ElderThemeDirective,
  ElderFormFieldLabelDirective,
  ElderSelectComponent,
  ElderQuantityInputControlComponent,
  ElderDimensionsInputComponent,
  ElderAuditedEntityComponent,
  ElderTableComponent,
  ElderHeaderComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderBasicPaneLayoutComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderPaneHeaderComponent,
} from '@elderbyte/ngx-starter'
import { Package, PackageLocation } from '../package'
import { PackageService } from '../package.service'
import { StorageOperationsService } from '../../operations/storage-operations.service'
import { StoragePath } from '../../storage-path'
import { StorageBrowseOptions } from '../../nodes/storage-node-browser/storage-node-browse-options'
import { StorageOperationResultType } from '../../operations/storage-operation'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatMenuTrigger, MatMenu } from '@angular/material/menu'
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
} from '@angular/material/table'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import { PackageStateSelectDirective } from '../package-state-select/package-state-select.directive'
import { MatInput } from '@angular/material/input'
import { MatFormField, MatLabel, MatPrefix, MatHint } from '@angular/material/form-field'
import { FormsModule } from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton, MatButton } from '@angular/material/button'
import { MatToolbar } from '@angular/material/toolbar'
import { NgTemplateOutlet, AsyncPipe, DatePipe } from '@angular/common'

@Component({
  selector: 'app-package-detail',
  templateUrl: './package-detail.component.html',
  styleUrls: ['./package-detail.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    FormsModule,
    MatFormField,
    ElderFormFieldLabelDirective,
    MatLabel,
    MatInput,
    MatPrefix,
    ElderSelectComponent,
    PackageStateSelectDirective,
    ElderQuantityInputControlComponent,
    ElderDimensionsInputComponent,
    ElderAuditedEntityComponent,
    MatHint,
    MatButton,
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    ElderHeaderComponent,
    MatMenuTrigger,
    MatMenu,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
    ElderPaneSubtitleComponent,
    ElderPaneTitleComponent,
    ElderPaneHeaderComponent,
  ],
})
export class PackageDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly package$: BehaviorSubject<Package> = new BehaviorSubject(null)

  public data: IDataContext<PackageLocation>

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  private router = inject(Router)

  private packageService = inject(PackageService)

  private storageOperationsService = inject(StorageOperationsService)

  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get package(): Package {
    return this.package$.getValue()
  }

  @Input()
  public set package(pkg: Package) {
    this.package$.next(pkg)
    if (pkg) {
      this.data = DataContextBuilder.start<PackageLocation>()
        .localSort((data, sorts) => SortUtil.sortData(data, sorts))
        .buildLocal(pkg.locationHistory, 'checkIn')

      this.data.start([new Sort('checkIn', 'desc')])
    }
  }

  public get deletionEnabled(): boolean {
    return true
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public onMove() {
    this.requestMove(this.package)
  }

  public canCheckout(): boolean {
    return !!this.package.location
  }

  public onRequestCheckout() {
    this.requestCheckout(this.package)
  }

  public onNavigateToStorageGroup(groupId: string): void {
    if (groupId) {
      this.router.navigate([
        {
          outlets: {
            side: ['storageGroups', groupId],
          },
        },
      ])
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private requestMove(pkg: Package): void {
    const options = pkg.location?.storagePath
      ? StorageBrowseOptions.start().withBrowsePath(
          StoragePath.parse(pkg.location.storagePath).facilityId
        )
      : null

    this.storageOperationsService.chooseLocationAndMove([pkg], options).subscribe({
      next: (operation) => {
        if (operation.result?.resultType == StorageOperationResultType.SUCCESS) {
          this.toastService.info('Successfully moved')
        } else {
          this.toastService.error('Failed to perform move!', operation.result?.message)
        }
        this.reload()
      },
      error: (err) => this.toastService.error('Failed to perform move!', err),
    })
  }

  private requestCheckout(pkg: Package): void {
    this.storageOperationsService.checkOut([pkg.id]).subscribe({
      next: () => {
        this.reload()
        this.toastService.info('Successfully checked out')
      },
      error: (err) => this.toastService.error('Failed to perform checkout!', err),
    })
  }

  private save(): void {
    this.toastService.info('Not yet implemented')
  }

  private emitClose(event: Event): void {
    this.close.emit(event)
  }

  private reload(): void {
    this.packageService.findById(this.package.id).subscribe(
      (pkg) => (this.package = pkg),
      (err) => this.toastService.error('messages.loading_failed', err)
    )
  }
}
